import { useState } from "react";
import { Target, Eye, Heart, Award, Users, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import QuoteModal from "@/components/QuoteModal";
import SEO from "@/components/SEO";

const HERO_IMAGE = "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=2000&q=80";
const LOGO_VERTICAL = "https://customer-assets.emergentagent.com/job_agglos-producer/artifacts/up3qekzf_3%281%29.png";

const values = [
  {
    icon: Award,
    title: "Qualité",
    description: "Nous nous engageons à fournir des matériaux de construction répondant aux plus hautes normes de qualité.",
  },
  {
    icon: Users,
    title: "Service Client",
    description: "Votre satisfaction est notre priorité. Nous offrons un accompagnement personnalisé à chaque client.",
  },
  {
    icon: TrendingUp,
    title: "Innovation",
    description: "Nous investissons continuellement dans nos équipements pour améliorer notre production.",
  },
  {
    icon: Heart,
    title: "Intégrité",
    description: "Nous construisons des relations durables basées sur la confiance et la transparence.",
  },
];

const milestones = [
  { year: "2023", title: "Création", desc: "Fondation de AGRABAT à Thiès" },
  { year: "2025", title: "Expansion", desc: "Ouverture du site de production d'agglos" },
];

export default function AboutPage() {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  return (
    <div className="min-h-screen" data-testid="about-page">
      <SEO 
        title="À Propos - Fabricant Agglos & Granulats au Sénégal"
        description="AGRABAT, fabricant d'agglos, parpaings et granulats de silex à Thiès depuis 2023. Production locale, carrière partenaire SILIKA GRANULATS à Darou Khoudoss."
        keywords="fabricant agglo Sénégal, producteur parpaing Thiès, carrière granulats silex, SILIKA GRANULATS, entreprise BTP Sénégal"
        url="https://agrabat.sn/a-propos"
      />
      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="about-hero"
      >
        <div className="absolute inset-0 bg-[#0A1320]/80" />
        <div className="relative z-10 text-center px-4">
          <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
            Qui sommes-nous
          </p>
          <h1 className="font-oswald text-5xl md:text-7xl font-bold text-white uppercase tracking-tight">
            À Propos
          </h1>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-white" data-testid="about-intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src={LOGO_VERTICAL}
                alt="AGRABAT"
                className="h-40 mb-8"
              />
              <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
                Notre Histoire
              </p>
              <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight mb-6">
                Bâtisseurs de<br />Confiance
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Depuis plus de 3 ans, AGRABAT s'est imposé comme un acteur majeur du secteur des matériaux 
                de construction au Sénégal. Basés à Thiès, nous produisons et commercialisons des granulats 
                de silex de haute qualité ainsi que des agglos pour répondre aux besoins du marché local.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Notre engagement envers la qualité et le service client nous a permis de construire des 
                relations durables avec des centaines de professionnels du BTP et de particuliers à 
                travers le pays.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Avec deux sites de production et livraison stratégiquement situés - notre unité d'agglos à Thiès et 
                notre carrière partenaire de granulats SILIKA à Darou Khoudoss - nous garantissons une disponibilité 
                constante et une livraison rapide de nos produits.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-square bg-slate-100 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/12276522/pexels-photo-12276522.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Maçon construisant un mur en agglos"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square bg-slate-100 overflow-hidden mt-8">
                <img
                  src="https://images.unsplash.com/photo-1768162485696-b67b380571a4?crop=entropy&cs=srgb&fm=jpg&w=600&q=85"
                  alt="Immeuble résidentiel moderne"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square bg-slate-100 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/4567377/pexels-photo-4567377.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Chantier de construction villa"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square bg-slate-100 overflow-hidden mt-8">
                <img
                  src="https://images.pexels.com/photos/35361410/pexels-photo-35361410.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Villa moderne terminée"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-slate-50" data-testid="mission-vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-white p-10 border-l-4 border-[#1E3A5F]">
              <Target className="w-12 h-12 text-[#FFD100] mb-6" />
              <h3 className="font-oswald text-2xl font-bold text-[#1E3A5F] uppercase mb-4">
                Notre Mission
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Fournir aux professionnels et particuliers du Sénégal des matériaux de construction 
                de qualité supérieure, tout en garantissant un service client exemplaire et des prix compétitifs.
              </p>
            </div>
            <div className="bg-white p-10 border-l-4 border-[#FFD100]">
              <Eye className="w-12 h-12 text-[#FFD100] mb-6" />
              <h3 className="font-oswald text-2xl font-bold text-[#1E3A5F] uppercase mb-4">
                Notre Vision
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Devenir le leader de la production et de la distribution de matériaux de construction 
                au Sénégal, reconnu pour notre engagement envers la qualité et la satisfaction client.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
              Ce qui nous guide
            </p>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight">
              Nos Valeurs
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="text-center p-8"
                data-testid={`value-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1E3A5F] mb-6">
                  <value.icon className="w-8 h-8 text-[#FFD100]" />
                </div>
                <h3 className="font-oswald text-xl font-semibold text-[#1E3A5F] uppercase mb-3">
                  {value.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#1E3A5F]" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FFD100] font-medium uppercase tracking-widest mb-4">
              Notre Parcours
            </p>
            <h2 className="font-oswald text-4xl md:text-5xl font-bold text-white uppercase tracking-tight">
              Moments Clés
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                className="text-center"
                data-testid={`milestone-${index}`}
              >
                <div className="font-oswald text-6xl font-bold text-[#FFD100] mb-4">
                  {milestone.year}
                </div>
                <h3 className="font-oswald text-xl font-semibold text-white uppercase mb-2">
                  {milestone.title}
                </h3>
                <p className="text-slate-400 text-sm">
                  {milestone.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50" data-testid="about-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-[#1E3A5F] uppercase tracking-tight mb-6">
            Travaillons Ensemble
          </h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Vous avez un projet de construction ? Contactez-nous pour discuter de vos besoins en matériaux.
          </p>
          <Button
            onClick={() => setIsQuoteModalOpen(true)}
            className="bg-[#FFD100] text-[#1E3A5F] hover:bg-[#E6BC00] rounded-none px-8 py-6 text-lg font-bold uppercase tracking-wider"
            data-testid="about-quote-btn"
          >
            Demander un Devis
          </Button>
        </div>
      </section>

      <QuoteModal isOpen={isQuoteModalOpen} onClose={() => setIsQuoteModalOpen(false)} />
    </div>
  );
}
